import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getTrace, type Trace } from "../api/client";
import type { ServiceSlug } from "../api/client";

export interface UseTraceOptions {
  serviceSlug: ServiceSlug | null;
  traceId: string | null | undefined;
  refreshToken?: number;
  enabled?: boolean;
}

export interface UseTraceResult {
  trace: Trace | null;
  loading: boolean;
  isFetching: boolean;
  error: string | null;
}

/**
 * Hook for fetching a single trace by ID.
 *
 * @param options - Configuration options for fetching a trace
 * @returns Object containing trace, loading state, and error
 */
export function useTrace({
  serviceSlug,
  traceId,
  refreshToken = 0,
  enabled = true,
}: UseTraceOptions): UseTraceResult {
  const query = useQuery({
    queryKey: ["trace", serviceSlug, traceId, refreshToken],
    enabled: !!serviceSlug && !!traceId && enabled,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => getTrace(traceId!, serviceSlug!, signal),
  });

  if (!serviceSlug || !traceId || !enabled) {
    return { trace: null, loading: false, isFetching: false, error: null };
  }

  const error =
    query.error instanceof Error
      ? query.error.message
      : query.error
        ? "Failed to load trace"
        : null;
  return {
    trace: query.data ?? null,
    loading: query.isLoading,
    isFetching: query.isFetching,
    error,
  };
}
